import React, { useState } from "react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import Pills, { PillType } from "../../controls/Pills";
import { isReadOnlyUser } from "../../../shared/utility";
import { makePostApiCall } from "../../../shared/makeApiCall";
import Button from "../../controls/Button";
import CopyableIdentifier from "../../controls/CopyableIdentifier";

export default function CycleCountItemsInRightPlace(props) {
  const {
    multiActionCall,
    items,
    type,
    selectedItems,
    setSelectedItems,
    multiActionText,
    cycleId,
    warehouseLocationId,
    search,
  } = props;

  const reportInProgress = false;

  const [expandedRows, setExpandedRows] = useState(null);
  const [groupedItems, setGroupedItems] = useState({});
  const [loadingRows, setLoadingRows] = useState({});

  // Level 3: Individual items (pallets and cartons) for lot number rows
  // Now per-parent: { [parentUniqueId]: expandedRowsObjectForThatNestedTable }
  const [expandedLotRows, setExpandedLotRows] = useState({});
  const [individualItems, setIndividualItems] = useState({});
  const [loadingLotRows, setLoadingLotRows] = useState({});

  const bodyProductName = (item) => {
    return (
      <div className="flex gap-2">
        {item.is_legacy === 1 ? <p className="text-lg my-auto">L</p> : null}
        <div>
          {item.product_name} <br />
          <span className="font-bold">PLU: {item.plu}</span>
        </div>
      </div>
    );
  };

  const moreActions = (item) => {
    return (
      <div>
        {isReadOnlyUser() ? null : (
          <Button
            className="ml-5"
            onClick={() => {
              multiActionCall(item);
            }}
            loading={reportInProgress}
          >
            {multiActionText}
          </Button>
        )}
      </div>
    );
  };

  function showMissingPill(item) {
    let content = null;
    if (item.status === 4) {
      content = (
        <Pills
          content={{
            type: PillType.Error,
            text: "Marked Missing",
          }}
        />
      );
    }
    return content;
  }

  const rowStyleClass = (data) => {
    return {
      "bg-cyan-100": data.pallet_id > 0,
      "bg-yellow-100": data.is_legacy === 1,
      "bg-gray-100": data.is_legacy === 0 && data.pallet_id < 1,
    };
  };

  const bodyCartonCount = (data) => {
    return <p>{data.total_cartons} cartons</p>;
  };

  // Get the Level 2 endpoint (grouped by PLU and lot) based on table type
  const getEndpoint = () => {
    switch (type) {
      case 1:
        return "cycleCounts/correctItemsGroupedByPluAndLot";
      case 2:
        return "cycleCounts/wrongItemsGroupedByPluAndLot";
      case 3:
        return "cycleCounts/missingItemsGroupedByPluAndLot";
      case 4:
        return "cycleCounts/shippedItemsGroupedByPluAndLot";
      case 5:
        return "cycleCounts/lockedItemsGroupedByPluAndLot";
      default:
        return "";
    }
  };

  // Get the Level 3 endpoint (individual items) based on table type
  const getIndividualItemsEndpoint = () => {
    switch (type) {
      case 1:
        return "cycleCounts/correctItemsByPluLotAndWarehouse";
      case 2:
        return "cycleCounts/wrongItemsByPluLotAndWarehouse";
      case 3:
        return "cycleCounts/missingItemsByPluLotAndWarehouse";
      case 4:
        return "cycleCounts/shippedItemsByPluLotAndWarehouse";
      case 5:
        return "cycleCounts/lockedItemsByPluLotAndWarehouse";
      default:
        return "";
    }
  };

  const onRowExpand = async (event) => {
    const rowData = event.data;
    const rowKey = rowData.unique_id;

    // If data already fetched, don't fetch again
    if (groupedItems[rowKey]) {
      return;
    }

    setLoadingRows((prev) => ({ ...prev, [rowKey]: true }));

    const payload = {
      plu: rowData.plu,
      cycle_id: cycleId,
      warehouse_location_id:
        rowData.warehouse_location_id || warehouseLocationId,
      warehouse_id: rowData.warehouse_id,
      search: search,
    };

    const endpoint = getEndpoint();
    const callResult = await makePostApiCall(endpoint, payload);

    if (callResult.success) {
      setGroupedItems((prev) => ({
        ...prev,
        [rowKey]: callResult.data,
      }));
    }

    setLoadingRows((prev) => ({ ...prev, [rowKey]: false }));
  };

  const allowExpansion = (rowData) => {
    return true;
  };

  const rowExpansionTemplate = (data) => {
    const rowKey = data.unique_id;
    const itemsForParent = groupedItems[rowKey] || [];
    const isLoading = loadingRows[rowKey];

    if (isLoading) {
      return (
        <div className="p-4 flex justify-center">
          <ProgressSpinner style={{ width: "30px", height: "30px" }} />
        </div>
      );
    }

    if (!itemsForParent || itemsForParent.length === 0) {
      return (
        <div className="p-4 text-sm text-gray-500">No lot numbers found</div>
      );
    }

    const nestedBodyProductName = (item) => {
      return (
        <div className="flex gap-2">
          {item.is_legacy === 1 ? <p className="text-lg my-auto">L</p> : null}
          <div>
            {item.product_name} <br />
            <span className="font-bold">PLU: {item.plu}</span>
          </div>
        </div>
      );
    };

    const nestedBodyCartonCount = (item) => {
      return <p>{item.total_cartons} cartons</p>;
    };

    // Handler for expanding lot number rows to show individual items
    const onLotRowExpand = async (event) => {
      const lotRowData = event.data;
      const lotRowKey = lotRowData.unique_id;

      // If data already fetched, don't fetch again
      if (individualItems[lotRowKey]) {
        return;
      }

      setLoadingLotRows((prev) => ({ ...prev, [lotRowKey]: true }));

      const payload = {
        plu: lotRowData.plu,
        lot_number: lotRowData.lot_number,
        cycle_id: cycleId,
        warehouse_location_id:
          lotRowData.warehouse_location_id || warehouseLocationId,
        warehouse_id: lotRowData.warehouse_id,
        search: search,
      };

      const endpoint = getIndividualItemsEndpoint();
      const callResult = await makePostApiCall(endpoint, payload);

      if (callResult.success) {
        setIndividualItems((prev) => ({
          ...prev,
          [lotRowKey]: callResult.data,
        }));
      }

      setLoadingLotRows((prev) => ({ ...prev, [lotRowKey]: false }));
    };

    // Template for individual items (Level 3)
    const individualItemsTemplate = (lotRowData) => {
      const lotRowKey = lotRowData.unique_id;
      const items = individualItems[lotRowKey] || [];
      const isLoading = loadingLotRows[lotRowKey];

      if (isLoading) {
        return (
          <div className="p-4 flex justify-center">
            <ProgressSpinner style={{ width: "30px", height: "30px" }} />
          </div>
        );
      }

      if (!items || items.length === 0) {
        return (
          <div className="p-4 text-sm text-gray-500">
            No items found for this lot number
          </div>
        );
      }

      const individualBodyBarcode = (item) => {
        return (
          <CopyableIdentifier
            value={item.barcode}
            description="full barcode"
          />
        );
      };

      const individualBodyType = (item) => {
        return <p>{item.is_pallet === 1 ? "Pallet" : "Carton"}</p>;
      };

      const individualBodyCartonCount = (item) => {
        return <p>{item.total_cartons} cartons</p>;
      };

      const individualRowStyleClass = (row) => {
        return {
          "bg-yellow-100": row.is_legacy === 1,
        };
      };

      return (
        <div>
          <DataTable
            value={items}
            dataKey="unique_id"
            tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
            cellClassName="py-2 bg-gray-100 border-1-bottom-gray px-2 text-sm"
            rowClassName={individualRowStyleClass}
            selectionMode="checkbox"
            selection={selectedItems}
            onSelectionChange={(e) => setSelectedItems(e.value)}
          >
            {type !== 1 ? (
                <Column
                  selectionMode="multiple"
                  headerStyle={{ width: "1px" }}
                ></Column>
              ) : null}
              
            <Column
              header="Name"
              body={nestedBodyProductName}
              style={{ width: "25%" }}
            />
            <Column
              header="Barcode"
              body={individualBodyBarcode}
              style={{ width: "20%" }}
            />
            <Column
              header="Type"
              body={individualBodyType}
              style={{ width: "10%" }}
            />
            <Column
              header="Total Cartons"
              body={individualBodyCartonCount}
              style={{ width: "12%", textAlign: "right" }}
            />
            <Column
              header="Location"
              field="warehouse_location_name"
              style={{ width: "15%" }}
            />

            {type === 3 ? <Column body={showMissingPill} /> : null}

            {type === 2 ? <Column body={moreActions} /> : null}
          </DataTable>
        </div>
      );
    };

    const allowLotExpansion = (rowData) => {
      return true;
    };

    return (
      <div>
        <DataTable
          value={itemsForParent}
          dataKey="unique_id"
          tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
          cellClassName="py-2 bg-gray-100 border-1-bottom-gray px-2 text-sm"
          expandedRows={expandedLotRows[rowKey] || null}
          onRowToggle={(e) =>
            setExpandedLotRows((prev) => ({
              ...prev,
              [rowKey]: e.data,
            }))
          }
          onRowExpand={onLotRowExpand}
          rowExpansionTemplate={individualItemsTemplate}
        >
          <Column
            header="Name"
            body={nestedBodyProductName}
            style={{ width: "25%" }}
          />
          <Column
            header="Lot number"
            body={(item) => (
              <CopyableIdentifier
                value={item.lot_number}
                description="lot number"
              />
            )}
            style={{ width: "10%" }}
          />
          <Column
            header="Carton Count"
            body={nestedBodyCartonCount}
            style={{ width: "10%", textAlign: "right" }}
          />
          <Column
            header="Location"
            field="warehouse_location_name"
            style={{ width: "15%" }}
          />
          <Column expander={allowLotExpansion} style={{ width: "10%" }} />
        </DataTable>
      </div>
    );
  };

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-4">
          <div className="overflow-hidden card-custom bg-white">
            <DataTable
              value={items}
              dataKey="unique_id"
              tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
              cellClassName="p-2 border-1-bottom-gray text-sm"
              scrollable
              scrollHeight={"800px"}
              rowClassName={rowStyleClass}
              expandedRows={expandedRows}
              onRowToggle={(e) => {
                setExpandedRows(e.data);
              }}
              onRowExpand={onRowExpand}
              rowExpansionTemplate={rowExpansionTemplate}
            >
              <Column
                header="Name"
                body={bodyProductName}
                style={{ width: "25%" }}
              />

              <Column
                header="Carton Count"
                body={bodyCartonCount}
                style={{ width: "10%", textAlign: "right" }}
              />

              <Column header="Location" field="warehouse_location_name" />

              <Column expander={allowExpansion} style={{ width: "10%" }} />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  );
}
